import cx from "classnames";
import { type FC, memo, useMemo } from "react";

import { type ActiveUser as ActiveUserType } from "@moment/api-collab/api-types";
import { AppTopBarButton } from "@moment/design-system/AppTopBarButton";
import { AppTopBarItem } from "@moment/design-system/AppTopBarItem";
import { AvatarSize } from "@moment/design-system/Avatar";
import { AvatarGroup } from "@moment/design-system/AvatarGroup";
import { PopOver } from "@moment/design-system/PopOver";
import { PopOverMenu } from "@moment/design-system/PopOverMenu";
import { PopOverMenuItem } from "@moment/design-system/PopOverMenuItem";

import { ActiveUser } from "./ActiveUser";

const BaseActiveUsers: FC = () => {
	const activeUsers: ActiveUserType[] = useMemo(() => [], []);
	const sortedActiveUsers = useMemo(() => activeUsers ?? [], [activeUsers]);

	if (sortedActiveUsers.length === 0) {
		return null;
	}

	return (
		<PopOver
			content={
				<PopOverMenu>
					{sortedActiveUsers.map((activeUser) => {
						if (activeUser.userID === undefined) {
							return null;
						}

						return (
							<PopOverMenuItem
								key={activeUser.userID}
								className={"flex items-center space-x-2 px-4 py-2 text-sm"}
							>
								<ActiveUser activeUser={activeUser} showName />
							</PopOverMenuItem>
						);
					})}
				</PopOverMenu>
			}
		>
			{(
				{ togglePopOver }: { togglePopOver?: React.MouseEventHandler<HTMLButtonElement> },
				ref: React.Ref<unknown>
			) => (
				<AppTopBarItem ref={ref}>
					<AppTopBarButton
						variant={"normal"}
						level={"secondary"}
						onClick={togglePopOver}
						maximizeHitArea={false}
						className={cx(
							"group",
							// ! is necessary because of the avatar cutouts
							"!rounded-none",
							"!py-px",
							"!transition-none",
							"bg-primary",
							"hover:!bg-secondary",
							"focus:!bg-tertiary",
							"active:!bg-tertiary"
						)}
					>
						<AvatarGroup
							max={4}
							style={{
								maxHeight: "100%",
								alignItems: "center",
								overflowY: "hidden",
							}}
							avatarSize={AvatarSize.sm}
						>
							{sortedActiveUsers.map((activeUser) => (
								<ActiveUser key={activeUser.userID} activeUser={activeUser} />
							))}
						</AvatarGroup>
					</AppTopBarButton>
				</AppTopBarItem>
			)}
		</PopOver>
	);
};

export const ActiveUsers = memo(BaseActiveUsers);
